import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { SKILL_LABEL } from "@/lib/gameData";
import { PawPrint, Sparkles, Lock } from "lucide-react";

export function PetsPanel() {
  const { snap, content } = useGame();
  const pets = content?.pets || {};
  const unlocked = new Set(snap?.pets?.unlocked || []);
  const active = snap?.pets?.active;

  const setActive = useCallback(async (petId) => {
    try {
      await api.post("/api/game/pets/active", { pet_id: petId });
      const { data } = await api.get("/api/game/state");
      // Trigger snap refresh via GameContext by polling — cheap here.
      toast.success(petId ? `${pets[petId]?.name} follows you now.` : "Companion dismissed.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [pets]);

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="pets-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl heading-gilded mb-1">Pets & Companions</h2>
          <p className="text-stone-500 font-body">
            Ultra-rare drops from skills and combat. Active pets grant a small permanent XP bonus.
          </p>
        </div>
        <div className="text-xs font-numeric text-amber-300">
          {unlocked.size} / {Object.keys(pets).length} unlocked
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.entries(pets).map(([id, p]) => {
          const owned = unlocked.has(id);
          const isActive = active === id;
          const art = `/generated/pet_${id}.png`;
          return (
            <article
              key={id}
              className={
                "panel p-4 flex flex-col items-center text-center gap-2 " +
                (isActive ? "border-amber-500 ring-1 ring-amber-500/40" : owned ? "" : "opacity-60")
              }
              data-testid={`pet-${id}`}
            >
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-amber-700 relative">
                <img
                  src={art}
                  alt={p.name}
                  className={"w-full h-full object-cover " + (owned ? "" : "grayscale opacity-40")}
                />
                {!owned && (
                  <div className="absolute inset-0 flex items-center justify-center bg-stone-950/50">
                    <Lock size={20} className="text-stone-500" />
                  </div>
                )}
              </div>
              <div className="font-display text-lg heading-gilded" data-testid={`pet-${id}-name`}>
                {owned ? p.name : "???"}
              </div>
              <div className="text-[11px] font-numeric text-stone-500 uppercase tracking-widest">
                {SKILL_LABEL[p.source] || p.source}  ·  1/{p.drop_rate}
              </div>
              <div className="text-xs text-stone-400 font-body italic min-h-[32px]">{owned ? p.desc : "Unlock by training its skill."}</div>
              <div className="flex items-center gap-1 text-xs text-emerald-400 font-numeric">
                <Sparkles size={10} /> +{Math.round(p.buff * 100)}% XP
              </div>
              {owned && (
                <button
                  onClick={() => setActive(isActive ? null : id)}
                  className={isActive ? "btn-ghost-gold text-xs px-3 py-1" : "btn-gold text-xs px-3 py-1"}
                  data-testid={`pet-toggle-${id}`}
                >
                  {isActive ? "Dismiss" : "Set active"}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
